import { Link, useRouter } from "@tanstack/react-router"
import { useId } from "react"

import { useCaptchaToken } from "@repo/captcha/use-captcha-token"
import { useResetPassword } from "@repo/supabase/hooks/use-reset-password"
import { Button, buttonVariants } from "@repo/ui/components/ui/button"
import {
  type FieldErrors,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  useForm,
} from "@repo/ui/components/ui/form"
import { Input } from "@repo/ui/components/ui/input"
import { toast } from "@repo/ui/components/ui/sonner"
import { logger } from "@repo/utils/logger"
import {
  type ForgotPasswordData,
  forgotPasswordSchema,
} from "~/modules/auth/auth.schemas"
import { useQuickPasswordUpdate } from "./use-quick-password-update"

const log = logger.child({ module: "web-app/forgot-password" })

export type ForgotPasswordFormProps = {
  email?: string
}

export function ForgotPasswordForm({ email }: ForgotPasswordFormProps) {
  const toastId = useId()
  const router = useRouter()
  const { getCaptchaTokenAsync, resetCaptchaToken } = useCaptchaToken()

  const form = useForm<ForgotPasswordData>({
    formSchema: forgotPasswordSchema,
    defaultValues: {
      email: email || "",
    },
  })

  const resetPassword = useResetPassword()

  // This is just a quality of life feature to make the user experience better.
  // It's not required for the password reset flow to work.
  useQuickPasswordUpdate()

  const onValid = async (data: ForgotPasswordData) => {
    toast.loading("Requesting password reset...", {
      id: toastId,
    })

    const location = router.buildLocation({
      to: "/auth/update-password",
    })

    resetPassword.mutate(
      {
        email: data.email,
        redirectTo: `${window.location.origin}${location.href}`,
        captchaToken: await getCaptchaTokenAsync(),
      },
      {
        onSuccess() {
          toast.success("Check Your Email", {
            description:
              "If you registered using your email and password, you will receive a password reset email.",
            id: toastId,
          })
        },
        onError(error) {
          log.error("Failed to send reset password request", { error })
          toast.error("Failed to send reset password request", {
            description: "Please check your email and try again",
            id: toastId,
          })
        },
        onSettled() {
          resetCaptchaToken()
        },
      },
    )
  }

  const onInvalid = (errors: FieldErrors<ForgotPasswordData>) => {
    log.error("Invalid form", { errors })
    toast.error("Invalid form", {
      description: "Please check your email and try again",
      id: toastId,
    })
  }

  return (
    <>
      {resetPassword.isSuccess ? (
        <div className="flex flex-col gap-6 rounded-lg border border-green-200 bg-green-100 p-6 text-green-950">
          <h3 className="font-semibold text-lg">Check Your Email</h3>
          <p className="text-muted-foreground text-sm">
            If you registered using your email and password, you will receive a
            password reset email.
          </p>
          <Link
            to="/auth/sign-in"
            className={buttonVariants({ variant: "solid" })}
          >
            Sign in
          </Link>
        </div>
      ) : (
        <Form {...form} onSubmit={form.handleSubmit(onValid, onInvalid)}>
          <div className="flex flex-col gap-6">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input
                      id="email"
                      type="email"
                      placeholder="joe@example.com"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {resetPassword.error && (
              <p className="rounded-md bg-destructive/50 px-2 py-1 text-destructive-foreground text-sm">
                {resetPassword.error.message}
              </p>
            )}
            <Button
              form={form.id}
              type="submit"
              className="w-full"
              isLoading={resetPassword.isPending}
              loadingText="Resetting password..."
            >
              Reset password
            </Button>
          </div>
          <div className="mt-4 text-center text-sm">
            Already have an account?{" "}
            <Link
              to="/auth/sign-in"
              className="underline underline-offset-4"
              viewTransition
            >
              Sign in
            </Link>
          </div>
        </Form>
      )}
    </>
  )
}
